import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface Props {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  trend?: { dir: "up" | "down" | "flat"; label: string };
  status?: { tone: "live" | "new" | "open" | "beta"; label: string };
  className?: string;
}

/**
 * Compact KPI card with optional icon and trend pill.
 * Pure presentation — no data fetching.
 */
export function StatCard({ label, value, hint, icon, trend, status, className }: Props) {
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-xl border border-border bg-card p-5 transition-all",
        "hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-px h-px"
        style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.4), transparent)" }}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        {icon ? (
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </div>
        ) : null}
      </div>
      <div className="mt-2 text-2xl font-black tracking-tight text-foreground md:text-3xl">
        {value}
      </div>
      {(hint || trend || status) && (
        <div className="mt-2 flex items-center gap-2">
          {trend ? (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                trend.dir === "up" && "bg-success/15 text-success",
                trend.dir === "down" && "bg-destructive/15 text-destructive",
                trend.dir === "flat" && "bg-muted text-muted-foreground",
              )}
            >
              {trend.dir === "up" ? "▲" : trend.dir === "down" ? "▼" : "—"} {trend.label}
            </span>
          ) : null}
          {status ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                status.tone === "live" && "bg-success/15 text-success",
                status.tone === "new" && "bg-primary/15 text-primary",
                status.tone === "open" && "bg-accent/20 text-accent-foreground",
                status.tone === "beta" && "bg-muted text-muted-foreground",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  status.tone === "live" && "bg-success animate-pulse",
                  status.tone === "new" && "bg-primary",
                  status.tone === "open" && "bg-accent-foreground/70",
                  status.tone === "beta" && "bg-muted-foreground/70",
                )}
              />
              {status.label}
            </span>
          ) : null}
          {hint ? (
            <span className="text-xs text-muted-foreground">{hint}</span>
          ) : null}
        </div>
      )}
    </div>
  );
}